<script lang="ts" setup>
  import {
    computed,
    resolveDirective,
    useSlots,
    type DirectiveBinding,
    type ObjectDirective,
    type VNode,
  } from 'vue';
  import { Tippy } from 'vue-tippy';

  interface Props {
    content?: string;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    theme?: string;
    trigger?: string;
    interactive?: boolean;
    focusable?: boolean;
  }

  // `focusable` puts the tabindex on the tippy REFERENCE, never on what the slot renders: the
  // default trigger is 'mouseenter focus', and focus never fires on a reference that cannot take
  // it. Making the slotted child focusable instead buys a focus stop that opens no tip.
  const props = defineProps<Props>();

  const slots = useSlots();
  const hasContentSlot = computed(() => Boolean(slots.content));

  const placement = computed(() => props.placement ?? 'top');
  const theme = computed(() => props.theme ?? 'custom');
  const trigger = computed(() => props.trigger ?? 'mouseenter focus');
  const interactive = computed(() => props.interactive ?? false);

  const tippyOptions = computed(() => ({
    content: props.content ?? '',
    placement: placement.value,
    theme: theme.value,
    trigger: trigger.value,
    interactive: interactive.value,
    // Content may go back to empty after the instance exists; tippy would open an empty box.
    onShow: (instance: { props: { content: unknown } }) => Boolean(instance.props.content),
  }));

  type TooltipOptions = typeof tippyOptions.value;
  type TooltipVNode = VNode<unknown, HTMLElement>;
  type Hook = (
    el: HTMLElement,
    binding: DirectiveBinding<TooltipOptions>,
    vnode: TooltipVNode,
    prevVNode: TooltipVNode | null
  ) => void;

  const registered = resolveDirective('tippy') as ObjectDirective<HTMLElement, TooltipOptions> | Hook | undefined;
  const tippy: ObjectDirective<HTMLElement, TooltipOptions> =
    typeof registered === 'function' ? { mounted: registered, updated: registered } : (registered ?? {});
  const withInstance = new WeakSet<HTMLElement>();

  // A caller may keep the tooltip mounted with no content so that the node it wraps never changes
  // type (a swap remounts the input inside and drops its focus). The tippy directive creates an
  // instance, a popper and its listeners on mount even with no content, one per field on a form,
  // so the instance is created only once there is something to show.
  function create(el: HTMLElement, binding: DirectiveBinding<TooltipOptions>, vnode: TooltipVNode): void {
    if (!binding.value.content) return;
    withInstance.add(el);
    tippy.mounted?.(el, binding, vnode, null);
  }

  const vTippyWhenFilled: ObjectDirective<HTMLElement, TooltipOptions> = {
    mounted: create,
    updated(el, binding, vnode, prevVNode) {
      if (withInstance.has(el)) tippy.updated?.(el, binding, vnode, prevVNode);
      else create(el, binding, vnode);
    },
    unmounted(el, binding, vnode, prevVNode) {
      if (withInstance.delete(el)) tippy.unmounted?.(el, binding, vnode, prevVNode);
    },
  };
</script>

<template>
  <Tippy
    v-if="hasContentSlot"
    :placement="placement"
    :theme="theme"
    :trigger="trigger"
    :interactive="interactive"
    :tabindex="focusable ? 0 : undefined"
  >
    <template #default>
      <slot />
    </template>
    <template #content>
      <slot name="content" />
    </template>
  </Tippy>
  <span
    v-else
    v-tippy-when-filled="tippyOptions"
    :tabindex="focusable ? 0 : undefined"
  >
    <slot />
  </span>
</template>
